import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   * Short-circuits the replacement when images are identical (e.g. size-only variant changes).
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // For products that use the PupSoul pet-variant-flow, trust the optimistic
    // update in variant-picker.js and ignore the server re-render entirely.
    // The optimistic update resolves the exact variant ID the user picked
    // against our client-side variant map — the server's
    // `selected_or_first_available_variant` can fall through to a different
    // variant's featured_media when inventory is tight or option_values is
    // ambiguous, which causes the "preview reverts" / "frame flickers black"
    // bugs. Our optimistic update is always authoritative for this product.
    const section = this.closest('.shopify-section, dialog');
    if (section?.querySelector('pet-variant-flow')) return;

    // Skip the destructive replaceWith when images are identical across variants
    if (this.#sameImages(newMediaGallery)) return;

    // Scope the refresh to slot #1 + the first thumbnail so slides 2..N keep
    // the original trust-building product media the shopper can still swipe to.
    // This also preserves any optimistic update applied in variant-picker.js.
    const newFirstSlideImg = newMediaGallery.querySelector('slideshow-slide img');
    const currentFirstSlide = this.querySelector('slideshow-slide');
    if (newFirstSlideImg instanceof HTMLImageElement && currentFirstSlide) {
      const newSrc = newFirstSlideImg.getAttribute('src') || '';
      const newSrcset = newFirstSlideImg.getAttribute('srcset') || '';
      currentFirstSlide.querySelectorAll('img').forEach((img) => {
        if (img instanceof HTMLImageElement) {
          if (newSrc) img.src = newSrc;
          if (newSrcset) img.srcset = newSrcset;
          else img.removeAttribute('srcset');
        }
      });
    }

    const newFirstThumbImg = newMediaGallery.querySelector('.slideshow-controls__thumbnail img');
    const currentFirstThumbImg = this.querySelector('.slideshow-controls__thumbnail img');
    if (newFirstThumbImg instanceof HTMLImageElement && currentFirstThumbImg instanceof HTMLImageElement) {
      const newSrc = newFirstThumbImg.getAttribute('src') || '';
      const newSrcset = newFirstThumbImg.getAttribute('srcset') || '';
      if (newSrc) currentFirstThumbImg.src = newSrc;
      if (newSrcset) currentFirstThumbImg.srcset = newSrcset;
      else currentFirstThumbImg.removeAttribute('srcset');
    }

    // Snap to slot #1 so a shopper who scrolled elsewhere sees their change.
    const firstThumbBtn = this.querySelector('.slideshow-controls__thumbnail');
    if (firstThumbBtn instanceof HTMLElement) firstThumbBtn.click();
  };

  /**
   * Compares image src URLs between the current and incoming gallery,
   * normalizing Shopify CDN transform params so that only the base image path is compared.
   *
   * @param {Element} newGallery - The incoming media gallery element.
   * @returns {boolean} True if both galleries contain the same images in the same order.
   */
  #sameImages(newGallery) {
    const normalize = (src) => {
      try {
        const url = new URL(src, window.location.origin);
        // Strip Shopify CDN transformation params (width, height, crop, v, format)
        url.searchParams.delete('width');
        url.searchParams.delete('height');
        url.searchParams.delete('crop');
        url.searchParams.delete('v');
        url.searchParams.delete('format');
        return url.pathname;
      } catch {
        return src;
      }
    };

    const currentSrcs = Array.from(this.querySelectorAll('img')).map((img) => normalize(img.src));
    const newSrcs = Array.from(newGallery.querySelectorAll('img')).map((img) => normalize(img.getAttribute('src') || ''));

    if (currentSrcs.length !== newSrcs.length) return false;
    return currentSrcs.every((src, i) => src === newSrcs[i]);
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
